import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ArrowUp, ArrowDown, Plus, Trash2 } from "lucide-react";
import { FIELD_TYPES, normalizeQuick } from "@/lib/quickEnquiry";

const ICON_CHOICES = ["plane", "visa", "hotel", "cars", "yachts", "attractions", "calendar", "users", "star", "gift", "heart", "pin"];
const slug = (s, taken) => {
  let base = (s || "item").toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "") || "item";
  let out = base, n = 2;
  while (taken.includes(out)) out = `${base}_${n++}`;
  return out;
};
const sel = "h-9 rounded-md px-2 text-sm bg-transparent";
const selStyle = { border: "1px solid var(--border-strong)" };
const muted = { color: "var(--text-muted)" };

function Rows({ items, onChange, render, addLabel, onAdd, testid }) {
  const move = (i, d) => { const n = [...items]; [n[i], n[i + d]] = [n[i + d], n[i]]; onChange(n); };
  return (
    <div className="space-y-2">
      {items.map((it, i) => (
        <div key={i} className="flex gap-2 items-start rounded-lg p-2" style={{ border: "1px solid var(--border)" }} data-testid={`${testid}-row-${i}`}>
          <div className="flex-1 min-w-0 space-y-2">{render(it, (v) => onChange(items.map((x, j) => (j === i ? v : x))), i)}</div>
          <div className="flex flex-col">
            <Button type="button" size="sm" variant="ghost" disabled={!i} onClick={() => move(i, -1)} aria-label="Move up"><ArrowUp className="h-3.5 w-3.5" /></Button>
            <Button type="button" size="sm" variant="ghost" disabled={i === items.length - 1} onClick={() => move(i, 1)} aria-label="Move down"><ArrowDown className="h-3.5 w-3.5" /></Button>
            <Button type="button" size="sm" variant="ghost" aria-label="Remove" data-testid={`${testid}-del-${i}`}
                    onClick={() => window.confirm("Remove this item?") && onChange(items.filter((_, j) => j !== i))}><Trash2 className="h-3.5 w-3.5 text-red-600" /></Button>
          </div>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" className="rounded-full" onClick={onAdd} data-testid={`${testid}-add`}>
        <Plus className="h-3.5 w-3.5 mr-1" />{addLabel}</Button>
    </div>
  );
}

function FieldRow({ f, set, lists, testid }) {
  const usesOptions = f.type === "search" || f.type === "select";
  return (
    <>
      <div className="grid sm:grid-cols-[1fr_auto] gap-2">
        <Input value={f.label} onChange={(e) => set({ ...f, label: e.target.value })} placeholder="Field label" data-testid={`${testid}-label`} />
        <select value={f.type} onChange={(e) => set({ ...f, type: e.target.value })} className={sel} style={selStyle} data-testid={`${testid}-type`}>
          {Object.entries(FIELD_TYPES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {usesOptions && (
        <select value={f.source || ""} onChange={(e) => set({ ...f, source: e.target.value || undefined })} className={`${sel} w-full`} style={selStyle} data-testid={`${testid}-source`}>
          <option value="">Own options (below)</option>
          {lists.map((l) => <option key={l.key} value={l.key}>Shared list: {l.label}</option>)}
        </select>
      )}
      {usesOptions && !f.source && (
        <Textarea rows={3} value={(f.options || []).join("\n")} placeholder="One option per line"
                  onChange={(e) => set({ ...f, options: e.target.value.split("\n").map((x) => x.slice(0, 80)) })}
                  onBlur={() => set({ ...f, options: (f.options || []).map((x) => x.trim()).filter(Boolean) })} data-testid={`${testid}-options`} />
      )}
      <div className="flex flex-wrap items-center gap-4 text-xs">
        {["search", "text", "textarea", "select"].includes(f.type) && (
          <Input value={f.placeholder || ""} onChange={(e) => set({ ...f, placeholder: e.target.value })} placeholder="Placeholder" className="h-8 max-w-[200px]" />
        )}
        {!["triptype", "travellers"].includes(f.type) && (
          <label className="inline-flex items-center gap-2"><Switch checked={!!f.required} onCheckedChange={(v) => set({ ...f, required: v })} data-testid={`${testid}-required`} />Required</label>
        )}
        {f.type === "date" && (
          <label className="inline-flex items-center gap-2"><Switch checked={!!f.returnOnly} onCheckedChange={(v) => set({ ...f, returnOnly: v })} />Only for Return trips</label>
        )}
      </div>
    </>
  );
}

export default function QuickCmsEditor({ value, onChange }) {
  const c = normalizeQuick(value);
  const up = (patch) => onChange({ ...c, ...patch, v2: true });
  const tabKeys = c.tabs.map((t) => t.key);
  const [serviceKeys, setServiceKeys] = useState([]);
  useEffect(() => { api.get("/config").then(({ data }) => setServiceKeys(Object.keys(data.service_rules || {}))).catch(() => {}); }, []);
  return (
    <div className="space-y-6" data-testid="quick-cms-editor">
      <label className="flex items-center gap-3 text-sm">
        <Switch checked={!!c.contact?.enabled} onCheckedChange={(v) => up({ contact: { enabled: v } })} data-testid="quick-cms-contact" />
        Ask guests for Name, Mobile Number and Email, plus Additional Notes for everyone (filled automatically for logged-in members)
      </label>

      <div>
        <div className="label-caps mb-2">Tabs & fields</div>
        <Rows items={c.tabs} testid="quick-cms-tab" addLabel="Add tab" onChange={(tabs) => up({ tabs })}
              onAdd={() => up({ tabs: [...c.tabs, { key: slug("new_tab", tabKeys), label: "New tab", icon: "star", fields: [] }] })}
              render={(t, setT, ti) => (
                <>
                  <div className="grid grid-cols-[1fr_auto] gap-2">
                    <Input value={t.label} onChange={(e) => setT({ ...t, label: e.target.value })} placeholder="Tab name" data-testid={`quick-cms-tab-${ti}-label`} />
                    <select value={t.icon} onChange={(e) => setT({ ...t, icon: e.target.value })} className={sel} style={selStyle} aria-label="Icon">
                      {ICON_CHOICES.map((x) => <option key={x} value={x}>{x}</option>)}
                    </select>
                  </div>
                  <label className="flex items-center gap-2 text-xs" style={muted}>
                    Points estimate uses reward rule
                    <select value={t.service || ""} onChange={(e) => setT({ ...t, service: e.target.value })} className={sel} style={selStyle}
                            data-testid={`quick-cms-tab-${ti}-service`}>
                      <option value="">Default rule</option>
                      {serviceKeys.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                  <div className="pl-3 space-y-1" style={{ borderLeft: "2px solid var(--border)" }}>
                    <div className="text-xs" style={muted}>Fields</div>
                    <Rows items={t.fields} testid={`quick-cms-tab-${ti}-field`} addLabel="Add field" onChange={(fields) => setT({ ...t, fields })}
                          onAdd={() => setT({ ...t, fields: [...t.fields, { label: "New field", type: "select", options: [], required: false, placeholder: "" }] })}
                          render={(f, setF, fi) => <FieldRow f={f} set={setF} lists={c.lists} testid={`quick-cms-tab-${ti}-field-${fi}`} />} />
                  </div>
                </>
              )} />
      </div>

      <div>
        <div className="label-caps mb-1">Shared option lists</div>
        <p className="text-xs mb-2" style={muted}>Edit once, every field using the list updates. Members can also type a value that isn't listed in searchable dropdowns.</p>
        <Rows items={c.lists} testid="quick-cms-list" addLabel="Add list" onChange={(lists) => up({ lists })}
              onAdd={() => up({ lists: [...c.lists, { key: slug("list", c.lists.map((l) => l.key)), label: "New list", options: [] }] })}
              render={(l, setL, li) => (
                <>
                  <Input value={l.label} onChange={(e) => setL({ ...l, label: e.target.value })} placeholder="List name" data-testid={`quick-cms-list-${li}-label`} />
                  <Textarea rows={4} value={l.options.join("\n")} placeholder="One option per line"
                            onChange={(e) => setL({ ...l, options: e.target.value.split("\n").map((x) => x.slice(0, 80)) })}
                            onBlur={() => setL({ ...l, options: [...new Set(l.options.map((x) => x.trim()).filter(Boolean))] })}
                            data-testid={`quick-cms-list-${li}-options`} />
                  <div className="text-xs" style={muted}>{l.options.filter((x) => x.trim()).length} options</div>
                </>
              )} />
      </div>

      <div>
        <div className="label-caps mb-1">Member budget ranges (points preview)</div>
        <p className="text-xs mb-2" style={muted}>Logged-in members pick a budget and see estimated points under the enquiry button. Leave "Max" empty for an open-ended range.</p>
        <Rows items={c.budgets} testid="quick-cms-budget" addLabel="Add range" onChange={(budgets) => up({ budgets })}
              onAdd={() => up({ budgets: [...c.budgets, { label: "New range", min: 0, max: null }] })}
              render={(b, setB, bi) => (
                <div className="grid grid-cols-[1fr_90px_90px] gap-2">
                  <Input value={b.label} onChange={(e) => setB({ ...b, label: e.target.value })} placeholder="Label" data-testid={`quick-cms-budget-${bi}-label`} />
                  <Input type="number" min="0" value={b.min ?? 0} onChange={(e) => setB({ ...b, min: Math.max(0, Number(e.target.value) || 0) })} placeholder="Min AED" aria-label="Min AED" />
                  <Input type="number" min="0" value={b.max ?? ""} onChange={(e) => setB({ ...b, max: e.target.value === "" ? null : Math.max(0, Number(e.target.value)) })} placeholder="Max AED" aria-label="Max AED" />
                </div>
              )} />
      </div>

      <div>
        <div className="label-caps mb-1">Popular routes</div>
        <p className="text-xs mb-2" style={muted}>One-tap chips that fill From and To and switch to the chosen tab.</p>
        <Rows items={c.routes} testid="quick-cms-route" addLabel="Add route" onChange={(routes) => up({ routes })}
              onAdd={() => up({ routes: [...c.routes, { label: "", tab: c.tabs[0]?.key || "flights", from: "", to: "" }] })}
              render={(r, setR, ri) => (
                <div className="grid sm:grid-cols-2 gap-2">
                  <Input value={r.label} onChange={(e) => setR({ ...r, label: e.target.value })} placeholder="Chip label, e.g. Dubai → Kochi" data-testid={`quick-cms-route-${ri}-label`} />
                  <select value={r.tab} onChange={(e) => setR({ ...r, tab: e.target.value })} className={sel} style={selStyle}>
                    {c.tabs.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
                  </select>
                  <Input value={r.from} onChange={(e) => setR({ ...r, from: e.target.value })} placeholder="From" list="quick-cms-cities" />
                  <Input value={r.to} onChange={(e) => setR({ ...r, to: e.target.value })} placeholder="To" list="quick-cms-cities" />
                </div>
              )} />
        <datalist id="quick-cms-cities">{(c.lists.find((l) => l.key === "cities")?.options || []).map((o) => <option key={o} value={o} />)}</datalist>
      </div>
    </div>
  );
}
